import { useQuery } from '@tanstack/react-query';
import type { Zone } from '@seo/contracts';
import { useTRPC } from './trpc';

export interface ZonePermission {
  label: string;
  level: string;
  read: boolean;
  write: boolean;
  delete: boolean;
  execute: boolean;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  /** `null` — роль у базі, якої ця збірка не знає; акаунт відмовлено всюди. */
  role: string | null;
  roleLabel: string;
  geoScope: string[];
  active: boolean;
  twoFactorEnabled: boolean;
  /** super_admin, який ще не увімкнув TOTP: усі процедури, крім `session.me`, відмовляють. */
  twoFactorSetupRequired: boolean;
  /**
   * Чи є локальний пароль, який можна підтвердити. `false` — обліковий запис заходить лише
   * через корпоративний SSO, і `/setup-2fa` просить замість пароля свіжий SSO-вхід.
   */
  hasPassword: boolean;
  zones: Record<Zone, ZonePermission>;
}

/**
 * The session query is the SPA's single source of identity and permissions. It is short
 * `staleTime` on purpose: a role change or a deactivation should reach the UI within a
 * minute, not at the end of a seven-day cookie.
 */
export function useSessionQuery() {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.session.me.queryOptions(),
    staleTime: 60_000,
    retry: false,
  });
}

export function useSession(): SessionUser | null {
  const { data } = useSessionQuery();
  return (data as SessionUser | null | undefined) ?? null;
}

export function useCan(zone: Zone, action: keyof Omit<ZonePermission, 'label' | 'level'>): boolean {
  const session = useSession();
  return session?.zones[zone]?.[action] === true;
}
